import { useState } from 'react';
import { Image, AlignLeft, MessageSquare, Eye, EyeOff, Plus, Trash2, GripVertical, User, ChevronDown, ChevronRight } from 'lucide-react';

export interface EmbedField {
  name: string;
  value: string;
  inline: boolean;
}

export interface EmbedData {
  title: string;
  title_url: string;
  description: string;
  color: string;
  author_name: string;
  author_icon_url: string;
  author_url: string;
  thumbnail_url: string;
  image_url: string;
  footer: string;
  footer_icon_url: string;
  timestamp: boolean;
  fields: EmbedField[];
}

export const EMPTY_EMBED: EmbedData = {
  title: '',
  title_url: '',
  description: '',
  color: '#FF6B9D',
  author_name: '',
  author_icon_url: '',
  author_url: '',
  thumbnail_url: '',
  image_url: '',
  footer: '',
  footer_icon_url: '',
  timestamp: false,
  fields: [],
};

interface EmbedBuilderProps {
  embed: EmbedData;
  onChange: (embed: EmbedData) => void;
  variables?: { key: string; label: string }[];
  compact?: boolean;
}

const DEFAULT_VARIABLES = [
  { key: '{user}', label: 'User Mention' },
  { key: '{user.name}', label: 'Username' },
  { key: '{server}', label: 'Server Name' },
  { key: '{memberCount}', label: 'Member Count' },
];

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = true }: {
  title: string;
  icon: any;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-300 hover:text-white bg-dark-800/40 hover:bg-dark-800/60 transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Icon className="w-4 h-4" />
        {title}
      </button>
      {open && <div className="p-4 space-y-4 bg-dark-900/30">{children}</div>}
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, maxLength, type = 'text', mono = false }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  type?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-400 mb-1.5 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full px-3 py-2 bg-dark-800 border border-white/[0.08] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 transition-colors ${mono ? 'font-mono' : ''}`}
      />
      {maxLength && (
        <p className="text-[10px] text-gray-700 mt-0.5 text-right">{value.length}/{maxLength}</p>
      )}
    </div>
  );
}

export default function EmbedBuilder({ embed, onChange, variables = DEFAULT_VARIABLES, compact = false }: EmbedBuilderProps) {
  const [showPreview, setShowPreview] = useState(true);

  // Ensure backwards compat — fill in missing fields from EMPTY_EMBED
  const e: EmbedData = { ...EMPTY_EMBED, ...embed, fields: embed.fields || [] };

  function update(field: keyof EmbedData, value: any) {
    onChange({ ...e, [field]: value });
  }

  function updateField(index: number, key: keyof EmbedField, value: any) {
    const fields = [...e.fields];
    fields[index] = { ...fields[index], [key]: value };
    update('fields', fields);
  }

  function addField() {
    if (e.fields.length >= 25) return;
    update('fields', [...e.fields, { name: '', value: '', inline: false }]);
  }

  function removeField(index: number) {
    update('fields', e.fields.filter((_: any, i: number) => i !== index));
  }

  function insertVariable(variable: string) {
    update('description', e.description + variable);
  }

  function previewText(text: string): string {
    return text
      .replace(/\{user\}/g, '@YameroSenpai')
      .replace(/\{user\.mention\}/g, '@YameroSenpai')
      .replace(/\{user\.name\}/g, 'YameroSenpai')
      .replace(/\{server\}/g, 'My Awesome Server')
      .replace(/\{server\.name\}/g, 'My Awesome Server')
      .replace(/\{memberCount\}/g, '1,234')
      .replace(/\{member\.count\}/g, '1,234');
  }

  // Simple markdown-ish rendering for preview
  function renderMarkdown(text: string): string {
    return previewText(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code style="background:#1e1f22;padding:1px 4px;border-radius:3px;font-size:0.85em">$1</code>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" style="color:#00b0f4;text-decoration:none">$1</a>');
  }

  return (
    <div className={`grid gap-6 ${compact ? '' : 'lg:grid-cols-2'}`}>
      {/* ─── Editor Column ─── */}
      <div className="space-y-3">
        {/* Author Section */}
        <CollapsibleSection title="Author" icon={User} defaultOpen={!!(e.author_name)}>
          <InputField label="Author Name" value={e.author_name} onChange={(v) => update('author_name', v)} placeholder="Author name" maxLength={256} />
          <div className="grid grid-cols-2 gap-3">
            <InputField label="Author URL" value={e.author_url} onChange={(v) => update('author_url', v)} placeholder="https://..." type="url" />
            <InputField label="Author Icon URL" value={e.author_icon_url} onChange={(v) => update('author_icon_url', v)} placeholder="https://..." type="url" />
          </div>
        </CollapsibleSection>

        {/* Body Section */}
        <CollapsibleSection title="Body" icon={AlignLeft} defaultOpen={true}>
          <InputField label="Title" value={e.title} onChange={(v) => update('title', v)} placeholder="Embed title" maxLength={256} />
          <InputField label="Title URL" value={e.title_url} onChange={(v) => update('title_url', v)} placeholder="https://..." type="url" />

          <div>
            <label className="text-xs font-medium text-gray-400 mb-1.5 block">Description</label>
            <textarea
              value={e.description}
              onChange={(ev) => update('description', ev.target.value)}
              placeholder="Supports **bold**, *italic*, `code`, [links](url)"
              maxLength={4096}
              rows={5}
              className="w-full px-3 py-2 bg-dark-800 border border-white/[0.08] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 transition-colors resize-y"
            />
            <p className="text-[10px] text-gray-700 mt-0.5 text-right">{e.description.length}/4096</p>
          </div>

          {/* Variable Buttons */}
          {variables.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-500 mb-1.5 uppercase tracking-wider">Insert Variables</p>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    title={v.label}
                    className="px-2 py-0.5 text-[11px] font-mono bg-dark-700 border border-white/[0.06] rounded text-primary/80 hover:text-primary hover:bg-dark-600 transition-colors"
                  >
                    {v.key}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color */}
          <div>
            <label className="text-xs font-medium text-gray-400 mb-1.5 block">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={e.color || '#FF6B9D'}
                onChange={(ev) => update('color', ev.target.value)}
                className="w-8 h-8 rounded-md border border-white/[0.08] cursor-pointer bg-transparent shrink-0"
              />
              <input
                type="text"
                value={e.color}
                onChange={(ev) => update('color', ev.target.value)}
                placeholder="#FF6B9D"
                className="w-24 px-2 py-1.5 bg-dark-800 border border-white/[0.08] rounded-md text-white text-xs font-mono focus:outline-none focus:border-primary/50 transition-colors"
              />
              <p className="text-[10px] text-gray-600">Any hex color</p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Fields Section */}
        <CollapsibleSection title={`Fields (${e.fields.length}/25)`} icon={GripVertical} defaultOpen={e.fields.length > 0}>
          {e.fields.map((field: EmbedField, i: number) => (
            <div key={i} className="bg-dark-800/50 rounded-lg p-3 space-y-2 border border-white/[0.04]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Field {i + 1}</span>
                <button type="button" onClick={() => removeField(i)} className="p-1 text-gray-600 hover:text-red-400 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <InputField label="Name" value={field.name} onChange={(v) => updateField(i, 'name', v)} placeholder="Field name" maxLength={256} />
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Value</label>
                <textarea
                  value={field.value}
                  onChange={(ev) => updateField(i, 'value', ev.target.value)}
                  placeholder="Field value"
                  maxLength={1024}
                  rows={2}
                  className="w-full px-3 py-2 bg-dark-800 border border-white/[0.08] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 transition-colors resize-y"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.inline}
                  onChange={(ev) => updateField(i, 'inline', ev.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-600 bg-dark-800 text-primary focus:ring-primary/30"
                />
                <span className="text-xs text-gray-400">Inline</span>
              </label>
            </div>
          ))}
          {e.fields.length < 25 && (
            <button
              type="button"
              onClick={addField}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-dashed border-white/[0.1] rounded-lg text-gray-500 hover:text-primary hover:border-primary/30 text-xs transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Field
            </button>
          )}
        </CollapsibleSection>

        {/* Images Section */}
        <CollapsibleSection title="Images" icon={Image} defaultOpen={!!(e.thumbnail_url || e.image_url)}>
          <InputField label="Thumbnail URL" value={e.thumbnail_url} onChange={(v) => update('thumbnail_url', v)} placeholder="https://... (small, top-right)" type="url" />
          <InputField label="Image URL" value={e.image_url} onChange={(v) => update('image_url', v)} placeholder="https://... (large, bottom)" type="url" />
        </CollapsibleSection>

        {/* Footer Section */}
        <CollapsibleSection title="Footer" icon={MessageSquare} defaultOpen={!!(e.footer)}>
          <InputField label="Footer Text" value={e.footer} onChange={(v) => update('footer', v)} placeholder="Footer text" maxLength={2048} />
          <InputField label="Footer Icon URL" value={e.footer_icon_url} onChange={(v) => update('footer_icon_url', v)} placeholder="https://..." type="url" />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={e.timestamp}
              onChange={(ev) => update('timestamp', ev.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-600 bg-dark-800 text-primary focus:ring-primary/30"
            />
            <span className="text-xs text-gray-400">Show Timestamp</span>
          </label>
        </CollapsibleSection>
      </div>

      {/* ─── Preview Column ─── */}
      <div className={compact ? '' : 'lg:sticky lg:top-24 lg:self-start'}>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-white transition-colors mb-2"
        >
          {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>

        {showPreview && (
          <div className="bg-[#313338] rounded-lg p-4 border border-[#3f4147]">
            <p className="text-[10px] text-gray-600 mb-3 font-medium uppercase tracking-wider">Discord Preview</p>
            <div className="flex gap-0">
              {/* Color bar */}
              <div className="w-1 rounded-l-md shrink-0" style={{ backgroundColor: e.color || '#FF6B9D' }} />
              {/* Embed body */}
              <div className="bg-[#2b2d31] rounded-r-md p-3 flex-1 min-w-0 max-w-[520px]">
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    {/* Author */}
                    {e.author_name && (
                      <div className="flex items-center gap-1.5 mb-1">
                        {e.author_icon_url && (
                          <img src={e.author_icon_url} alt="" className="w-5 h-5 rounded-full object-cover" onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <span className="text-xs text-white font-medium">{previewText(e.author_name)}</span>
                      </div>
                    )}
                    {/* Title */}
                    {e.title && (
                      <p className={`font-semibold text-sm ${e.title_url ? 'text-[#00b0f4] hover:underline cursor-pointer' : 'text-white'}`}>
                        {previewText(e.title)}
                      </p>
                    )}
                    {/* Description */}
                    {e.description && (
                      <p
                        className="text-[#dcddde] text-[13px] whitespace-pre-wrap break-words leading-[1.375rem]"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(e.description) }}
                      />
                    )}
                    {/* Fields */}
                    {e.fields.length > 0 && (
                      <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: e.fields.some((f: EmbedField) => f.inline) ? 'repeat(3, 1fr)' : '1fr' }}>
                        {e.fields.map((field: EmbedField, i: number) => (
                          <div key={i} className={field.inline ? '' : 'col-span-full'}>
                            {field.name && <p className="text-xs text-white font-semibold">{previewText(field.name)}</p>}
                            {field.value && (
                              <p className="text-xs text-[#dcddde] whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderMarkdown(field.value) }} />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Thumbnail */}
                  {e.thumbnail_url && (
                    <img src={e.thumbnail_url} alt="" className="w-[80px] h-[80px] rounded object-cover shrink-0 mt-1" onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                </div>
                {/* Image */}
                {e.image_url && (
                  <img src={e.image_url} alt="" className="mt-3 w-full max-h-[300px] rounded object-cover" onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                {/* Footer */}
                {(e.footer || e.timestamp) && (
                  <div className="flex items-center gap-1.5 mt-2">
                    {e.footer_icon_url && (
                      <img src={e.footer_icon_url} alt="" className="w-4 h-4 rounded-full object-cover" onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }} />
                    )}
                    <span className="text-[#b5bac1] text-[11px]">
                      {e.footer && previewText(e.footer)}
                      {e.footer && e.timestamp && ' • '}
                      {e.timestamp && new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
